import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Settings,
  Plus,
  Sparkles,
  FileText,
  Edit,
  Trash2,
  Check,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import API_BASE_URL from '../config/api';

const INJURY_TYPES = [
  { value: '', label: 'All / General' },
  { value: 'knee', label: 'Knee' },
  { value: 'shoulder', label: 'Shoulder' },
  { value: 'ankle', label: 'Ankle' },
  { value: 'spine', label: 'Spine' },
  { value: 'hip', label: 'Hip' },
  { value: 'wrist', label: 'Wrist' },
  { value: 'elbow', label: 'Elbow' },
  { value: 'general', label: 'General' }
];

const emptyForm = {
  ruleText: '',
  injuryType: '',
  ageRangeMin: '',
  ageRangeMax: ''
};

function PreferencesPage() {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('rules');

  // Preferences / rules state
  const [preferences, setPreferences] = useState([]);
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [savingRule, setSavingRule] = useState(false);

  // Suggestions state
  const [suggestInjuryType, setSuggestInjuryType] = useState('knee');
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  // Edit history state
  const [edits, setEdits] = useState([]);
  const [loadingEdits, setLoadingEdits] = useState(false);
  const [editFilterType, setEditFilterType] = useState('');
  const [expandedEditId, setExpandedEditId] = useState(null);

  const [error, setError] = useState('');

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Fetch preferences
  const fetchPreferences = useCallback(async () => {
    setLoadingPrefs(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/preferences`, getAuthHeaders());
      setPreferences(response.data);
    } catch (err) {
      console.error('Error fetching preferences:', err);
      setError('Failed to load preference rules');
    } finally {
      setLoadingPrefs(false);
    }
  }, []);

  // Fetch edit history
  const fetchEdits = useCallback(async () => {
    setLoadingEdits(true);
    try {
      const params = new URLSearchParams();
      if (editFilterType) params.append('injuryType', editFilterType);
      params.append('limit', '20');
      const response = await axios.get(
        `${API_BASE_URL}/preferences/edits?${params.toString()}`,
        getAuthHeaders()
      );
      setEdits(response.data);
    } catch (err) {
      console.error('Error fetching edits:', err);
    } finally {
      setLoadingEdits(false);
    }
  }, [editFilterType]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  useEffect(() => {
    if (activeTab === 'edits') {
      fetchEdits();
    }
  }, [activeTab, fetchEdits]);

  // Rule CRUD handlers
  const handleSaveRule = async (e) => {
    e.preventDefault();
    if (!formData.ruleText.trim()) {
      setError('Rule text is required');
      return;
    }
    setSavingRule(true);
    setError('');
    try {
      const body = {
        ruleText: formData.ruleText.trim(),
        injuryType: formData.injuryType || null,
        ageRangeMin: formData.ageRangeMin ? Number(formData.ageRangeMin) : null,
        ageRangeMax: formData.ageRangeMax ? Number(formData.ageRangeMax) : null
      };

      if (editingId) {
        await axios.put(`${API_BASE_URL}/preferences/${editingId}`, body, getAuthHeaders());
      } else {
        await axios.post(`${API_BASE_URL}/preferences`, body, getAuthHeaders());
      }

      setShowAddForm(false);
      setEditingId(null);
      setFormData({ ...emptyForm });
      fetchPreferences();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save rule');
    } finally {
      setSavingRule(false);
    }
  };

  const handleEditRule = (pref) => {
    setFormData({
      ruleText: pref.ruleText,
      injuryType: pref.injuryType || '',
      ageRangeMin: pref.ageRangeMin != null ? String(pref.ageRangeMin) : '',
      ageRangeMax: pref.ageRangeMax != null ? String(pref.ageRangeMax) : ''
    });
    setEditingId(pref.id);
    setShowAddForm(true);
    setError('');
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Are you sure you want to delete this preference rule?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/preferences/${id}`, getAuthHeaders());
      fetchPreferences();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete rule');
    }
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingId(null);
    setFormData({ ...emptyForm });
    setError('');
  };

  // AI suggestion handler
  const handleSuggestRules = async () => {
    setSuggesting(true);
    setSuggestions([]);
    setError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/preferences/suggest-rules?injuryType=${suggestInjuryType}`,
        {},
        getAuthHeaders()
      );
      setSuggestions(response.data.suggestions || response.data || []);
    } catch (err) {
      console.error('Error suggesting rules:', err);
      setError('Failed to generate AI suggestions');
    } finally {
      setSuggesting(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion) => {
    try {
      await axios.post(`${API_BASE_URL}/preferences`, {
        ruleText: suggestion.ruleText,
        injuryType: suggestion.injuryType || suggestInjuryType,
        ageRangeMin: suggestion.ageRangeMin || null,
        ageRangeMax: suggestion.ageRangeMax || null
      }, getAuthHeaders());
      setSuggestions(prev => prev.filter(s => s !== suggestion));
      fetchPreferences();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create rule from suggestion');
    }
  };

  const handleDismissSuggestion = (suggestion) => {
    setSuggestions(prev => prev.filter(s => s !== suggestion));
  };

  // Truncate text helper
  const truncate = (text, maxLen = 200) => {
    if (!text) return '';
    return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
  };

  // Loading spinner
  if (loadingPrefs && activeTab === 'rules') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <header style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '16px 24px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              display: 'flex',
              alignItems: 'center',
              color: '#4b5563',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '8px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
          >
            <ArrowLeft style={{ width: '20px', height: '20px', marginRight: '8px' }} />
            Back to Dashboard
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Settings style={{ width: '28px', height: '28px', color: '#2563eb' }} />
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                Clinical Preferences
              </h1>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
                AI learning rules and letter edit history
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '24px 24px 0' }}>
        <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #e5e7eb' }}>
          <button
            onClick={() => setActiveTab('rules')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'rules' ? '#2563eb' : '#6b7280',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'rules' ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Settings style={{ width: '16px', height: '16px' }} />
            Preference Rules
          </button>
          <button
            onClick={() => setActiveTab('edits')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'edits' ? '#2563eb' : '#6b7280',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'edits' ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FileText style={{ width: '16px', height: '16px' }} />
            Edit History
          </button>
        </div>
      </div>

      <main style={{ maxWidth: '1280px', margin: '0 auto', padding: '24px' }}>
        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px'
          }}>
            <p style={{ color: '#dc2626', fontSize: '14px', margin: 0 }}>{error}</p>
          </div>
        )}

        {/* ========== TAB 1: Preference Rules ========== */}
        {activeTab === 'rules' && (
          <div>
            {/* Action bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowAddForm(true);
                  setEditingId(null);
                  setFormData({ ...emptyForm });
                  setError('');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 20px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
              >
                <Plus style={{ width: '18px', height: '18px', marginRight: '8px' }} />
                Add Rule
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <select
                  value={suggestInjuryType}
                  onChange={(e) => setSuggestInjuryType(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {INJURY_TYPES.filter(t => t.value).map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <button
                  onClick={handleSuggestRules}
                  disabled={suggesting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 20px',
                    backgroundColor: suggesting ? '#9ca3af' : '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: suggesting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => !suggesting && (e.currentTarget.style.backgroundColor = '#6d28d9')}
                  onMouseLeave={(e) => !suggesting && (e.currentTarget.style.backgroundColor = '#7c3aed')}
                >
                  <Sparkles style={{ width: '18px', height: '18px', marginRight: '8px' }} />
                  {suggesting ? 'Analysing...' : 'Suggest Rules'}
                </button>
              </div>
            </div>

            {/* Add / Edit form */}
            {showAddForm && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                padding: '24px',
                marginBottom: '20px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0', color: '#111827' }}>
                  {editingId ? 'Edit Rule' : 'Add New Rule'}
                </h3>
                <form onSubmit={handleSaveRule}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                      Rule Text *
                    </label>
                    <textarea
                      value={formData.ruleText}
                      onChange={(e) => setFormData({ ...formData, ruleText: e.target.value })}
                      placeholder="e.g. Always include exercise vital sign section for patients over 40"
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Injury Type
                      </label>
                      <select
                        value={formData.injuryType}
                        onChange={(e) => setFormData({ ...formData, injuryType: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          boxSizing: 'border-box'
                        }}
                      >
                        {INJURY_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Min Age
                      </label>
                      <input
                        type="number"
                        value={formData.ageRangeMin}
                        onChange={(e) => setFormData({ ...formData, ageRangeMin: e.target.value })}
                        placeholder="e.g. 18"
                        min="0"
                        max="120"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Max Age
                      </label>
                      <input
                        type="number"
                        value={formData.ageRangeMax}
                        onChange={(e) => setFormData({ ...formData, ageRangeMax: e.target.value })}
                        placeholder="e.g. 65"
                        min="0"
                        max="120"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      disabled={savingRule}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 24px',
                        backgroundColor: savingRule ? '#9ca3af' : '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: savingRule ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => !savingRule && (e.currentTarget.style.backgroundColor = '#15803d')}
                      onMouseLeave={(e) => !savingRule && (e.currentTarget.style.backgroundColor = '#16a34a')}
                    >
                      <Check style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                      {savingRule ? 'Saving...' : (editingId ? 'Update Rule' : 'Save Rule')}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelForm}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 24px',
                        backgroundColor: '#e5e7eb',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1d5db'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                    >
                      <X style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* AI Suggestions */}
            {suggesting && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                padding: '32px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #e5e7eb',
                  borderTop: '4px solid #7c3aed',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }}></div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>
                  AI is analysing letter edits for <strong>{suggestInjuryType}</strong> injuries...
                </p>
              </div>
            )}

            {suggestions.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#7c3aed', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Sparkles style={{ width: '18px', height: '18px' }} />
                  AI-Suggested Rules ({suggestions.length})
                </h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {suggestions.map((suggestion, idx) => (
                    <div key={idx} style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      padding: '16px 20px',
                      borderLeft: '4px solid #7c3aed'
                    }}>
                      <p style={{ fontSize: '14px', color: '#111827', margin: '0 0 8px 0', lineHeight: '1.5' }}>
                        {suggestion.ruleText}
                      </p>
                      {(suggestion.injuryType || suggestion.ageRangeMin || suggestion.ageRangeMax) && (
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                          {suggestion.injuryType && (
                            <span style={{
                              padding: '2px 10px',
                              fontSize: '12px',
                              fontWeight: '500',
                              borderRadius: '9999px',
                              backgroundColor: '#f3e8ff',
                              color: '#7c3aed'
                            }}>
                              {suggestion.injuryType}
                            </span>
                          )}
                          {(suggestion.ageRangeMin != null || suggestion.ageRangeMax != null) && (
                            <span style={{
                              padding: '2px 10px',
                              fontSize: '12px',
                              fontWeight: '500',
                              borderRadius: '9999px',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af'
                            }}>
                              Age: {suggestion.ageRangeMin || '0'} - {suggestion.ageRangeMax || '120+'}
                            </span>
                          )}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleAcceptSuggestion(suggestion)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px 16px',
                            backgroundColor: '#16a34a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
                        >
                          <Check style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                          Accept
                        </button>
                        <button
                          onClick={() => handleDismissSuggestion(suggestion)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px 16px',
                            backgroundColor: '#e5e7eb',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1d5db'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                        >
                          <X style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                          Dismiss
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rules table */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                  Active Rules ({preferences.length})
                </h2>
              </div>
              <div style={{ overflowX: 'auto' }}>
                {preferences.length === 0 ? (
                  <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
                    <Settings style={{ width: '48px', height: '48px', color: '#d1d5db', margin: '0 auto 12px' }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>No preference rules yet. Add a rule or let AI suggest some.</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f9fafb' }}>
                      <tr>
                        <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Rule</th>
                        <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Injury Type</th>
                        <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Age Range</th>
                        <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                        <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preferences.map((pref, index) => (
                        <tr key={pref.id} style={{ borderTop: index > 0 ? '1px solid #e5e7eb' : 'none' }}>
                          <td style={{ padding: '16px 24px', fontSize: '14px', color: '#111827', maxWidth: '400px' }}>
                            {pref.ruleText}
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            {pref.injuryType ? (
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                borderRadius: '9999px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: '#f3e8ff',
                                color: '#7c3aed'
                              }}>
                                {pref.injuryType.charAt(0).toUpperCase() + pref.injuryType.slice(1)}
                              </span>
                            ) : (
                              <span style={{ fontSize: '13px', color: '#6b7280' }}>All types</span>
                            )}
                          </td>
                          <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                            {(pref.ageRangeMin != null || pref.ageRangeMax != null)
                              ? `${pref.ageRangeMin || 0} - ${pref.ageRangeMax || '120+'}`
                              : <span style={{ color: '#6b7280' }}>Any age</span>
                            }
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '9999px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: pref.autoGenerated ? '#fef3c7' : '#dcfce7',
                              color: pref.autoGenerated ? '#92400e' : '#166534'
                            }}>
                              {pref.autoGenerated ? 'Auto-generated' : 'Active'}
                            </span>
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleEditRule(pref)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '6px 12px',
                                  backgroundColor: '#dbeafe',
                                  color: '#1e40af',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#bfdbfe'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                              >
                                <Edit style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteRule(pref.id)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '6px 12px',
                                  backgroundColor: '#fee2e2',
                                  color: '#dc2626',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fecaca'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                              >
                                <Trash2 style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ========== TAB 2: Edit History ========== */}
        {activeTab === 'edits' && (
          <div>
            {/* Filter bar */}
            <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Filter by injury type:</label>
              <select
                value={editFilterType}
                onChange={(e) => setEditFilterType(e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                {INJURY_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* Edits list */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                  Recent Letter Edits
                </h2>
              </div>

              {loadingEdits ? (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #2563eb',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 12px'
                  }}></div>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>Loading edit history...</p>
                </div>
              ) : edits.length === 0 ? (
                <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
                  <FileText style={{ width: '48px', height: '48px', color: '#d1d5db', margin: '0 auto 12px' }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>No letter edits found{editFilterType ? ` for ${editFilterType} injuries` : ''}.</p>
                </div>
              ) : (
                <div>
                  {edits.map((edit, index) => {
                    const isExpanded = expandedEditId === (edit.id || index);
                    return (
                      <div
                        key={edit.id || index}
                        style={{
                          borderTop: index > 0 ? '1px solid #e5e7eb' : 'none'
                        }}
                      >
                        {/* Edit summary row */}
                        <button
                          onClick={() => setExpandedEditId(isExpanded ? null : (edit.id || index))}
                          style={{
                            width: '100%',
                            padding: '16px 24px',
                            textAlign: 'left',
                            backgroundColor: isExpanded ? '#f9fafb' : 'white',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                          onMouseEnter={(e) => {
                            if (!isExpanded) e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            if (!isExpanded) e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
                            <FileText style={{ width: '20px', height: '20px', color: '#6b7280', flexShrink: 0 }} />
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <p style={{ fontSize: '14px', fontWeight: '500', color: '#111827', margin: 0 }}>
                                {edit.patientName || 'Unknown Patient'}
                              </p>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                                {edit.letterType && (
                                  <span style={{
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    borderRadius: '4px',
                                    backgroundColor: edit.letterType === 'mri_referral' ? '#dbeafe' : '#f3e8ff',
                                    color: edit.letterType === 'mri_referral' ? '#1e40af' : '#7c3aed'
                                  }}>
                                    {edit.letterType === 'mri_referral' ? 'Radiology Referral' : 'Consultation'}
                                  </span>
                                )}
                                {edit.injuryType && (
                                  <span style={{
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    borderRadius: '4px',
                                    backgroundColor: '#f3e8ff',
                                    color: '#7c3aed'
                                  }}>
                                    {edit.injuryType}
                                  </span>
                                )}
                                {edit.editedAt && (
                                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                    {new Date(edit.editedAt).toLocaleDateString('en-NZ')} {new Date(edit.editedAt).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {isExpanded
                            ? <ChevronUp style={{ width: '20px', height: '20px', color: '#6b7280', flexShrink: 0 }} />
                            : <ChevronDown style={{ width: '20px', height: '20px', color: '#6b7280', flexShrink: 0 }} />
                          }
                        </button>

                        {/* Expanded diff view */}
                        {isExpanded && (
                          <div style={{
                            padding: '0 24px 20px',
                            backgroundColor: '#f9fafb'
                          }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                              <div>
                                <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
                                  Original
                                </h4>
                                <div style={{
                                  backgroundColor: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  maxHeight: '300px',
                                  overflowY: 'auto'
                                }}>
                                  <pre style={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    margin: 0,
                                    color: '#374151'
                                  }}>
                                    {edit.originalContent ? truncate(edit.originalContent, 1000) : 'No original content available'}
                                  </pre>
                                </div>
                              </div>
                              <div>
                                <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
                                  Edited
                                </h4>
                                <div style={{
                                  backgroundColor: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  maxHeight: '300px',
                                  overflowY: 'auto'
                                }}>
                                  <pre style={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    margin: 0,
                                    color: '#374151'
                                  }}>
                                    {edit.editedContent ? truncate(edit.editedContent, 1000) : 'No edited content available'}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default PreferencesPage;
